import { isLocal, isLocalOverride, isPreprod } from "@/Environment";
import { isFreightSimpleEmployee } from "@/Helpers/isFreightSimpleEmployee";

const DEV_MODE_KEY = "dashboard:dev-mode";

/**
 * Reads `?dev=1` / `?dev=0` from the URL once at startup and persists the
 * choice for this browser. `?dev=1` turns developer mode on; `?dev=0` clears
 * it. Called from the app entry before anything reads the gate.
 */
export function syncDeveloperModeFromUrl(): void {
  const dev = new URLSearchParams(window.location.search).get("dev");
  if (dev === "1" || dev === "true") {
    localStorage.setItem(DEV_MODE_KEY, "true");
  } else if (dev === "0" || dev === "false") {
    localStorage.removeItem(DEV_MODE_KEY);
  }
}

/**
 * True when developer affordances should be available in the current
 * environment. Local dev (including running locally against a remote API via
 * VITE_LOCAL_OVERRIDE_ENVIRONMENT) and preprod always qualify; demo and
 * production require an explicit `?dev=1` opt-in (persisted per browser). This
 * keeps the dev menu / inspector / palette out of the way on the deployed
 * customer site even for employees, until they ask for them.
 */
function isDeveloperModeActive(): boolean {
  if (isLocal() || isPreprod() || isLocalOverride()) return true;
  return localStorage.getItem(DEV_MODE_KEY) === "true";
}

/**
 * The single gate for every developer affordance (dev menu, inspector, command
 * palette, cold-start overlay). The employee-email check is the hard backstop
 * so customers never see dev tools even with `?dev=1`; developer mode adds the
 * environment / opt-in layer on top.
 */
export function showDeveloperAffordances(email: string | undefined): boolean {
  return isFreightSimpleEmployee(email) && isDeveloperModeActive();
}
